import { featureMapForSpecimen, specimenFeatures, type FeatureMap } from './specimenFeatures';
import { getTaxon } from './taxonomyDatabase';

export interface SpecimenScore {
  score: number;
  featureScore: number;
  identificationCorrect: boolean | null;
  observedCount: number;
  matchedFeatures: string[];
  missedFeatures: string[];
  feedback: string;
}

/** Awards evidence-based partial credit; a species guess without observations earns no points. */
export function score(specimenId: string, observations: FeatureMap, claimedSpecimenId?: string): SpecimenScore {
  const expected = featureMapForSpecimen(specimenId);
  const observed = Object.entries(observations).filter(([key, value]) => value !== undefined && key in expected);
  const matchedFeatures = observed.filter(([key, value]) => value === expected[key]).map(([key]) => key);
  const missedFeatures = observed.filter(([key, value]) => value !== expected[key]).map(([key]) => key);
  const featureScore = observed.length ? Math.round((matchedFeatures.length / observed.length) * 80) : 0;
  const identificationCorrect = claimedSpecimenId === undefined ? null : claimedSpecimenId === specimenId;
  const result = observed.length ? Math.min(100, featureScore + (identificationCorrect ? 20 : 0)) : 0;
  const correctName = getTaxon(specimenId).commonName;
  const feedback = !observed.length
    ? 'Observe and record diagnostic features first; a lucky specimen guess alone receives no credit.'
    : identificationCorrect === false
      ? `The final identification is not correct, but ${matchedFeatures.length} evidence-based feature(s) were correctly observed. Compare conflicting traits and try again.`
      : identificationCorrect === true
        ? `Identification and feature evidence recorded. ${matchedFeatures.length} of ${observed.length} observations match ${correctName}.`
        : `${matchedFeatures.length} of ${observed.length} observed features match the reference specimen.`;
  return { score: result, featureScore, identificationCorrect, observedCount: observed.length, matchedFeatures, missedFeatures, feedback };
}

export function scoreCompleteness(specimenId: string, observations: FeatureMap): number {
  const diagnosticIds = specimenFeatures[specimenId].features.filter((item) => item.diagnosticFor.length > 0).map((item) => item.id);
  if (!diagnosticIds.length) return 0;
  return Math.round((diagnosticIds.filter((id) => id in observations).length / diagnosticIds.length) * 100);
}
